import React from 'react'
import { useDispatch, addNotification } from '@ggs/store'
import { useI18n } from '@ggs/i18n'
import { get, first } from 'lodash'
import { useApiRequest } from './'

/**
 * @typedef {import('../../definitions').CourseSectionsExpListParams} CourseSectionsListParams
 * @typedef {import('../../definitions').CourseSectionExpRecord} CourseSectionRecord
 * @typedef {import('../../definitions').CourseSectionsExpListResponse} CourseSectionsListResponse
 */

/**
 * @typedef {(params: CourseSectionsListParams) => Promise<CourseSectionsListResponse>} GetCourseSections
 * @typedef {(id: string, params?: CourseSectionsListParams) => Promise<CourseSectionRecord>} GetCourseSection
 */

/**
 * @typedef {Object} AcademicExpApiHook
 * @property {GetCourseSections} getCourseSections
 * @property {GetCourseSection} getCourseSection
 */

/**
 * Hook that expose functions to consume Academic API data.
 * @return {AcademicExpApiHook}
 */
function useAcademicExpApi() {
  const { t } = useI18n()
  const dispatch = useDispatch()
  const baseUrl = `${process.env.REACT_APP_ACADEMICEXP_BASE_URL}` || 'http://localhost'
  const { doApiRequest, prepareQueryParams } = useApiRequest({
    baseUrl,
  })

  /**
   * Process a API request.
   * @param {string} endpoint An API endpoint path
   * @param {any} data Payload data
   * @param {Object<string, string>=} customHeaders
   * @param {string} method
   * @return {Promise<any>}
   */
  const doRequest = async (endpoint, data = null, customHeaders, method = '') => {
    const proxyEndpoint = `?_api_proxy_uri=${encodeURIComponent(endpoint)}`
    const response = await doApiRequest({
      endpoint: proxyEndpoint,
      data,
      customHeaders,
      method,
    })

    const errorCode = get(response, 'errorCode', '')
    if (errorCode) {
      dispatch(
        addNotification(
          'academic_exp_api_request_failed',
          'error',
          t('Academic Experience API request failed.')
        )
      )

      return null
    }

    return response
  }

  /**
   * Get subject courses based on given params.
   * @type {GetCourseSections}
   * @param {CourseSectionsListParams} params
   * @return {Promise<CourseSectionsListResponse>}
   */
  const getCourseSections = async (params) => {
    const endpoint = 'course-section-details'
    const queryParams = prepareQueryParams({
      ...params,
      sectionStatus: ['Waitlist', 'Canceled', 'Closed', 'Open'],
    })

    const response = await doRequest(`${endpoint}?${queryParams}`)

    if (!response?.pageItems) {
      return null
    }

    return response
  }

  /**
   * Get course section by ID.
   * @type {GetCourseSection}
   * @param {string} id
   * @param {CourseSectionsListParams=} extraParams
   * @return {Promise<CourseSectionRecord>}
   */
  const getCourseSection = async (id, extraParams = {}) => {
    const results = await getCourseSections({ courseSectionId: [id], ...extraParams })

    if (!results) {
      return null
    }

    return first(results.pageItems)
  }

  return {
    getCourseSections,
    getCourseSection,
  }
}

export default useAcademicExpApi
